import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  callbackToXero,
  connectXero,
  getXeroTenantId,
  getXeroTax,
  sendToXero,
  getXeroCustomers,
  disconnectXero,
  getXeroServiceCategoryMappings,
  mapXeroServiceCategories,
  getXeroItems,
  getXeroRealmId,
  bulkSendToXero,
} from "controller/api";
import { ApiErrorType } from "shared";
import { sentryLogger } from "utilz";

export const useConnectXero = () => {
  return useMutation({
    mutationFn: () => connectXero(),
    onSuccess(data, variables, context) {},
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "useConnectXero",
      });
    },
  });
};

export const useCallbackToXero = () => {
  return useMutation({
    mutationFn: ({
      state,
      code,
      scope,
      session_state,
    }: {
      state: string;
      code: string;
      scope: string;
      session_state: string;
    }) =>
      callbackToXero({
        state,
        code,
        scope,
        session_state,
      }),
    onSuccess(data, variables, context) {},
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "useCallbackToXero",
      });
    },
  });
};

export const useGetXeroItems = (dropdown?: string) => {
  return useQuery({
    queryKey: ["xeroProducts", dropdown],
    queryFn: () => getXeroItems(dropdown),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "sendToXero",
      });
    },
  });
};

export const useGetXeroTax = () => {
  return useQuery({
    queryKey: ["xeroTax"],
    queryFn: () => getXeroTax(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        // data: variables,
        workflow: "integration",
        level: "error",
        functionName: "sendToXero",
      });
    },
  });
};

export const useSendToXero = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceId,
      currency,
      xeroRealmId,
    }: {
      invoiceId: string;
      currency: string;
      xeroRealmId: string;
    }) => sendToXero({ invoiceId, currency, xeroRealmId }),
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["invoices"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      queryClient.invalidateQueries(["invoices"]);
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "sendToXero",
      });
    },
  });
};

export const useBulkSendToXero = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      invoiceIds,
      currency,
      xeroRealmId,
    }: {
      invoiceIds: string[];
      currency: string;
      xeroRealmId: string;
    }) => bulkSendToXero({ invoiceIds, currency, xeroRealmId }),
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["invoices"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      queryClient.invalidateQueries(["invoices"]);
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "bulkSendToXero",
      });
    },
  });
};

export const useGetTenantId = () => {
  return useQuery({
    queryKey: ["tenantId"],
    queryFn: () => getXeroTenantId(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "getXeroTenantId",
      });
    },
  });
};

export const useGetXeroCustomers = (perPage: number = 100) => {
  return useInfiniteQuery({
    queryKey: ["xero", "customers", perPage],
    queryFn: ({ pageParam = 1 }) =>
      getXeroCustomers({
        page: pageParam as number,
        perPage,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = lastPage?.data?.length || 0;
      if (fetchedCount < perPage) return undefined;
      return allPages.length + 1;
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "getXeroCustomers",
      });
    },
  });
};

export const useDisconnectXero = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectXero,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["xero"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      queryClient.invalidateQueries(["xero"]);
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "disconnectXero",
      });
    },
  });
};

export const useGetXeroServiceCategoryMappings = () => {
  return useQuery({
    queryKey: ["xeroServiceCategoryMappings"],
    queryFn: () => getXeroServiceCategoryMappings(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "getXeroServiceCategoryMappings",
      });
    },
  });
};

export const useMapXeroServiceCategories = () => {
  const queryClient = useQueryClient();
  return useMutation((value: any) => mapXeroServiceCategories(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["xeroServiceCategoryMappings"]);
    },
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        data: variables,
        workflow: "integration",
        level: "error",
        functionName: "mapXeroServiceCategories",
      });
    },
  });
};

export const useGetXeroRealmId = () => {
  return useQuery({
    queryKey: ["xero-realmId"],
    queryFn: () => getXeroRealmId(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "integration",
        level: "error",
        functionName: "getXeroRealmId",
      });
    },
  });
};
